import React, { useMemo, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Loader2, Plus, Library, Search, FileText } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { hasOwnerAccess } from '../../lib/owner'
import { useReadingBooks } from '../../hooks/useReadingBooks'

const STATUSES = ['reading', 'wishlist', 'finished', 'paused']

// ReadingPage — /reading
//
// Shelf with a status filter strip across the top. Tap a card to drill
// into the book detail. Empty state nudges toward "+ add book".
export function ReadingPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user, loading: authLoading } = useAuth()
  const { books, loading } = useReadingBooks()
  const [filter, setFilter] = useState('reading')

  const counts = useMemo(() => {
    const c = { reading: 0, wishlist: 0, finished: 0, paused: 0 }
    for (const b of books) {
      if (c[b.status] !== undefined) c[b.status] += 1
    }
    return c
  }, [books])

  const filtered = useMemo(() => books.filter((b) => b.status === filter), [books, filter])

  if (!authLoading && user && !hasOwnerAccess(user)) {
    return <Navigate to="/" replace />
  }
  if (authLoading || loading) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin opacity-60" />
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-md space-y-4 p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <Library className="h-5 w-5 text-app-primary-500" />
          <h1 dir="auto" className="text-2xl font-semibold">{t('reading.title')}</h1>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => navigate('/reading/catalog')}
            className="inline-flex items-center gap-1 rounded-full bg-neutral-100 px-3 py-1.5 text-xs font-medium dark:bg-neutral-800"
          >
            <Search className="h-3.5 w-3.5" /> {t('reading.browseCatalog')}
          </button>
          <button
            type="button"
            onClick={() => navigate('/reading/new')}
            className="inline-flex items-center gap-1 rounded-full bg-rose-500 px-3 py-1.5 text-xs font-medium text-white"
          >
            <Plus className="h-3.5 w-3.5" /> {t('reading.addBook')}
          </button>
        </div>
      </div>

      <div className="flex gap-1.5 overflow-x-auto">
        {STATUSES.map((s) => {
          const active = filter === s
          return (
            <button
              key={s}
              type="button"
              onClick={() => setFilter(s)}
              className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                active
                  ? 'bg-rose-500 text-white'
                  : 'bg-neutral-100 text-foreground dark:bg-neutral-800'
              }`}
            >
              <span dir="auto">{t(`reading.status.${s}`)}</span>
              <span className="ms-1.5 opacity-60">{counts[s]}</span>
            </button>
          )
        })}
      </div>

      {filtered.length === 0 ? (
        <div className="py-10 text-center">
          <p dir="auto" className="text-sm opacity-70">{t('reading.emptyShelf')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          {filtered.map((b) => (
            <button
              key={b.id}
              type="button"
              onClick={() => navigate(`/reading/${b.id}`)}
              className="overflow-hidden rounded-2xl border border-neutral-200 bg-card text-start dark:border-neutral-800"
            >
              <div className="relative aspect-[2/3] bg-neutral-200 dark:bg-neutral-800">
                {b.cover_url ? (
                  <img src={b.cover_url} alt="" className="h-full w-full object-cover" />
                ) : (
                  <div className="grid h-full place-items-center">
                    <Library className="h-8 w-8 opacity-30" />
                  </div>
                )}
                {b.file_format && (
                  <span className="absolute end-1 top-1 inline-flex items-center gap-0.5 rounded-md bg-black/55 px-1.5 py-0.5 text-[9px] font-bold uppercase text-white" dir="ltr">
                    <FileText className="h-2.5 w-2.5" />{b.file_format}
                  </span>
                )}
              </div>
              <div className="space-y-0.5 p-2">
                <p dir="auto" className="line-clamp-2 text-sm font-medium leading-snug">{b.title}</p>
                {b.author && (
                  <p dir="auto" className="line-clamp-1 text-[11px] opacity-60">{b.author}</p>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
